// src/hooks/useVinDecoder.js
import { useState, useRef, useEffect } from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";

/**
 * useVinDecoder
 * @param {(mapped: {year:string,name:string,model:string}, onlyFillEmpty:boolean, sourceVin:string) => void} onVinDecoded
 * @param {object} opts - optional config
 *    { string } opts.baseUrl - e.g. Helpers.baseUrl (including trailing slash) OR '' for relative
 *    { object } opts.axiosConfig - e.g. Helpers.headers (should be an axios config object)
 *    { string } opts.endpointPrefix - default 'api/vin/' (set to 'vin/' if your route is /vin/:vin)
 *    { number } opts.debounceMs - default 700
 */
export function useVinDecoder(onVinDecoded, opts = {}) {
  const {
    baseUrl = Helpers.baseUrl, // example: 'http://localhost:4000/' or '' to use relative paths
    axiosConfig = {}, // example: Helpers.headers -> { headers: { ... } }
    endpointPrefix = "vin/",
    debounceMs = 700,
  } = opts;

  const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{11,17}$/;

  const [vinLoading, setVinLoading] = useState(false);
  const [vinError, setVinError] = useState("");

  const debounceRef = useRef(null);
  const abortRef = useRef(null); // stores current AbortController
  const onlyFillEmptyRef = useRef(true);

  const isVinValid = (vin) => VIN_REGEX.test((vin ?? "").toString().trim().toUpperCase());

  function buildVinEndpoint(safeVin) {
    // ensure baseUrl ends with slash or is empty, and endpointPrefix is concatenated properly
    let b = baseUrl || "";
    if (b && !b.endsWith("/")) b = b + "/";
    const prefix = endpointPrefix ? endpointPrefix.replace(/^\/+|\/+$/g, "") + "/" : "";
    return `${b}${prefix}${encodeURIComponent(safeVin)}`; // e.g. 'http://localhost:4000/api/vin/1HG...'
  }

  async function fetchVin(vin) {
    const safeVin = (vin ?? "").toString().trim().toUpperCase();
    if (!isVinValid(safeVin)) {
      setVinError("VIN looks invalid (11-17 chars, no I/O/Q)");
      return;
    }

    // cancel any previous inflight request
    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch (_) {}
      abortRef.current = null;
    }

    setVinError("");
    setVinLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const url = buildVinEndpoint(safeVin);

      // axios supports AbortController's signal
      const response = await axios.get(url, {
        signal: controller.signal,
        ...axiosConfig,
      });

      const json = response?.data ?? null;

      // expected backend shape: { ok: true, vin, data }
      if (!json || (json.ok === false)) {
        const msg = (json && json.message) || "VIN Decode failed";
        throw new Error(msg);
      }

      const data = json.data ?? json; // accept either {data: {...}} or raw result
      const mapped = {
        year: data?.ModelYear ?? data?.modelYear ?? "",
        name: data?.Make ?? data?.make ?? "",
        model: data?.Model ?? data?.model ?? "",
        engineSize: data?.DisplacementL ?? data?.displacementL ?? data?.EngineDisplacementL ?? "",
        engineCylinders: data?.EngineCylinders ?? data?.engineCylinders ?? "",
        drivetrain: data?.DriveType ?? data?.driveType ?? "",
      };

      // callback: allow consumer to decide how to merge (onlyFillEmptyRef indicates behavior)
      onVinDecoded(mapped, onlyFillEmptyRef.current, safeVin);
    } catch (err) {
      // axios throws an error we may want to ignore if aborted/cancelled
      const name = err?.name ?? (err?.code ? String(err.code) : undefined);
      if (name === "CanceledError" || name === "AbortError") {
        // request was cancelled — ignore
      } else {
        console.error("VIN lookup error:", err);
        setVinError("VIN lookup failed. Try again or enter manually.");
      }
    } finally {
      setVinLoading(false);
      abortRef.current = null;
    }
  }

  function onVinChange(vin) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (isVinValid(vin)) fetchVin(vin);
    }, debounceMs);
  }

  function onVinBlur(vin) {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (isVinValid(vin)) fetchVin(vin);
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) {
        try {
          abortRef.current.abort();
        } catch (_) {}
        abortRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    vinLoading,
    vinError,
    onlyFillEmptyRef, // consumer can toggle .current
    onVinChange,
    onVinBlur,
    fetchVin, // exposed in case consumer wants to call manually
  };
}
